import React, { useEffect, useMemo } from "react";
import { TextField } from "@mui/material";
import moment from "moment";
import TableFilter from "components/Shared/AppTable/TableFilter";
import BaseSelect from "components/Shared/Base/BaseSelect";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useCustomParams } from "hooks/useCustomParams";
import { useLazyGetDDLCustomerQuery } from "store/services/customerApi";
import { useLazyGetDDLVendorListReportQuery } from "store/services/reportApi";
import { useAuth } from "hooks";

const DATE_RANGE_OPTIONS = [
  { label: "Last 7 Days", value: "last7days" },
  { label: "Last Month", value: "lastMonth" },
  { label: "Custom", value: "custom" },
];

const getRange = (range) => ({
  fromDate: range === "lastMonth"
    ? moment().subtract(1, "month").format("YYYY-MM-DD")
    : moment().subtract(6, "days").format("YYYY-MM-DD"),
  toDate: moment().format("YYYY-MM-DD"),
});

const ShipmentChargeFilter = ({ disabled = false }) => {
  const { params, appendParams } = useCustomParams();
  const { viewEntity } = useAuth();
  const [fetchVendors, { data: vendorsData }] = useLazyGetDDLVendorListReportQuery();
  const [fetchCustomers, { data: customersData = [] }] = useLazyGetDDLCustomerQuery();

  useEffect(() => {
    if (viewEntity?.entity?._id) {
      fetchVendors({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
        status: "Active",
      });
      fetchCustomers({
        account: viewEntity.entity.account._id,
        entity: viewEntity.entity._id,
      });
    }
  }, [fetchVendors, fetchCustomers, viewEntity]);

  useEffect(() => {
    if (!params.timeRange) appendParams({ timeRange: "last7days", ...getRange("last7days") });
  }, [params.timeRange, appendParams]);

  const vendors = useMemo(() => vendorsData?.data || vendorsData || [], [vendorsData]);
  const customers = useMemo(() => customersData?.data || customersData || [], [customersData]);
  const selectedVendor = vendors.find((vendor) => (vendor.value || vendor._id) === params.vendorId) || null;
  const selectedCustomer = customers.find((customer) => customer._id === params.customerId) || null;

  const handleRange = (value) => {
    appendParams({ timeRange: value, ...(value === "custom" ? {} : getRange(value)) });
  };

  return (
    <>
      <TableFilter searchLabel="Shipment ID" disabled={disabled} initialFilters={3}>
        <BaseSelect
          fullWidth label="Date Range" value={params.timeRange || "last7days"}
          options={DATE_RANGE_OPTIONS}
          getOptionLabel={(option) => option?.label || ""}
          getOptionValue={(option) => option?.value || ""}
          onChange={(event) => handleRange(event.target.value)}
        />
        {params.timeRange === "custom" && <>
          <TextField label="From Date" type="date" size="small" fullWidth
            value={params.fromDate || ""} InputLabelProps={{ shrink: true }}
            inputProps={{ max: params.toDate || moment().format("YYYY-MM-DD") }}
            onChange={(event) => appendParams({ fromDate: event.target.value })} />
          <TextField label="To Date" type="date" size="small" fullWidth
            value={params.toDate || ""} InputLabelProps={{ shrink: true }}
            inputProps={{ min: params.fromDate || undefined, max: moment().format("YYYY-MM-DD") }}
            onChange={(event) => appendParams({ toDate: event.target.value })} />
        </>}
        <BaseAutocomplete fullWidth label="Vendor" options={vendors} value={selectedVendor}
          onChange={(_, value) => appendParams({ vendorId: value?.value || value?._id || "" })}
          getOptionLabel={(option) => option?.label || option?.shopName || option?.name || ""}
          isOptionEqualToValue={(option, value) => (option.value || option._id) === (value.value || value._id)} />
        <BaseAutocomplete fullWidth label="Customer" options={customers} value={selectedCustomer}
          onChange={(_, value) => appendParams({ customerId: value?._id || "" })}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, value) => option?._id === value?._id} />
        <BaseSelect fullWidth label="Sort" value={params.sort || "totalCharge:-1"}
          options={[
            { label: "Highest Charge", value: "totalCharge:-1" },
            { label: "Lowest Charge", value: "totalCharge:1" },
            { label: "Newest", value: "createdAt:-1" },
          ]}
          getOptionLabel={(option) => option?.label || ""}
          getOptionValue={(option) => option?.value || ""}
          onChange={(event) => appendParams({ sort: event.target.value })} />
      </TableFilter>
    </>
  );
};

export default ShipmentChargeFilter
